import { trackMetaPriceCheck } from "../../utils/metaPixel";
// pages/user/Calculator.jsx

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSEO } from "../../hooks/useSEO";
import { useTheme } from "../../theme/ThemeProvider";
import { SELL_DEVICE_ROUTE } from "../../routes/constant";
import {
  fetchFaults,
  calculateDevicePrice,
  nextStep,
  prevStep,
  toggleFault,
  toggleOption,
  toggleUpgrade,
  setDeviceAge,
  clearCalculator,
} from "../../store/slices/calculatorSlice";
import Layout from "../../components/Layout";
import DeviceAgeSelector from "../../components/user/lead/calculator/DeviceAgeSelector";
import FaultCategory from "../../components/user/lead/calculator/FaultCategory";
import NavigationButtons from "../../components/user/lead/calculator/NavigationButtons";
import DeviceInfoCard from "../../components/user/lead/calculator/DeviceInfoCard";
import UpgradeOptions from "../../components/user/lead/calculator/UpgradeOptions";
import LoginModal from "../../components/auth/LoginModal";
import toast from "react-hot-toast";
import CalculatorSkeleton from "../../components/user/lead/calculator/CalculatorSkeleton";
import { getAuthToken } from "../../utils/authUtils";

const Calculator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const { categorySlug, brandSlug, productSlug, variantSlug } = useParams();

  const { isLoggedIn } = useSelector((state) => state.auth);
  const { selectedCity } = useSelector((state) => state.city);

  const cityId = selectedCity?.id;

  const {
    productData,
    variation,
    upgradeOptions,
    faultCategories,
    selectedFaults,
    selectedOptions,
    selectedUpgrades,
    deviceAge,
    loading,
    calculating,
    currentStep,
    stepCategories,
    ageRanges,
    error,
  } = useSelector((state) => state.calculator);

  const [showLoginModal, setShowLoginModal] = useState(false);

  const calculationParams = useRef(null);
  const hasRedirected = useRef(false);
  const isCalculating = useRef(false); // NEW: Prevent double API calls

  // Step configuration
  const hasUpgrades = upgradeOptions && upgradeOptions.length > 0;

  // Get total steps
  const totalSteps = stepCategories.length + (hasUpgrades ? 1 : 0) + 1;
  const isLastStep = currentStep === totalSteps - 1;

  // Determine current step types
  const isUpgradeStep = hasUpgrades && currentStep === 0;
  const isAgeStep = hasUpgrades ? currentStep === 1 : currentStep === 0;

  // Calculate fault step index
  let faultStepIndex;
  if (hasUpgrades) {
    faultStepIndex = currentStep - 2;
  } else {
    faultStepIndex = currentStep - 1;
  }

  // Get current fault categories for this step
  const currentCategories =
    faultStepIndex >= 0 && faultStepIndex < stepCategories.length
      ? stepCategories[faultStepIndex] || []
      : [];

  // Check if any fault is selected in current step
  const hasSelectedFaultsInStep = currentCategories.some((category) => {
    return category.faults?.some((fault) => selectedFaults[fault.id] === true);
  });

  const getDeviceAgeValue = () => {
    return deviceAge || "";
  };
  // ============= SEO - Only after data loads =============
  const seoParams = useMemo(() => {
    if (loading || !productData) return null;

    const variationName =
      variation?.title ||
      productData?.variation_name ||
      variantSlug?.replace(/-/g, " ") ||
      "";
    const productName =
      productData?.product_name || productData?.product?.title || "";
    const brandName = productData?.brand?.name || productData?.brand_name || "";
    const categoryName =
      productData?.category?.name || productData?.category_name || "";

    return {
      categorySlug: categorySlug,
      brandSlug: brandSlug,
      productSlug: productSlug,
      variantSlug: variantSlug,
      categoryName: categoryName,
      brandName: brandName,
      productName: productName,
      variantName: variationName,
    };
  }, [
    loading,
    productData,
    variation,
    variantSlug,
    categorySlug,
    brandSlug,
    productSlug,
  ]);

  useSEO(
    `/${categorySlug}/${brandSlug}/${productSlug}/${variantSlug}/check-price`,
    seoParams,
  );

  useEffect(() => {
    if (variantSlug && productSlug) {
      dispatch(fetchFaults({ brandSlug, productSlug, variantSlug }));
    } else {
      toast.error("Missing variation information");
    }
    return () => {
      dispatch(clearCalculator());
      isCalculating.current = false;
    };
  }, [dispatch, variantSlug, productSlug, cityId]);

  const executeCalculation = async (params) => {
    // Auth token is persisted when OTP verification/registration succeeds.
    // Do not rely on a render-time isLoggedIn snapshot: this callback may run
    // before React has rendered the Redux login state change.
    if (!getAuthToken()) {
      calculationParams.current = params;
      setShowLoginModal(true);
      return;
    }
    // Prevent duplicate API calls
    if (hasRedirected.current || isCalculating.current) {
      return;
    }

    isCalculating.current = true;

    try {
      const resultAction = await dispatch(calculateDevicePrice(params));

      if (calculateDevicePrice.fulfilled.match(resultAction)) {
        trackMetaPriceCheck(resultAction.payload?.evaluation_id);
        hasRedirected.current = true;
        navigate(
          SELL_DEVICE_ROUTE(categorySlug, brandSlug, productSlug, variantSlug),
        );
      } else if (resultAction.error) {
        const errorMessage = resultAction.payload || resultAction.error.message;
        if (
          typeof errorMessage === "string" &&
          (errorMessage.toLowerCase().includes("login") ||
            errorMessage.toLowerCase().includes("authenticate"))
        ) {
          setShowLoginModal(true);
        } else {
          toast.error(errorMessage || "Failed to calculate price");
        }
      }
    } finally {
      isCalculating.current = false;
    }
  };

  const handleCalculate = async () => {
    // Prevent if already calculating
    if (isCalculating.current) {
      return;
    }

    const faultIds = Object.keys(selectedFaults)
      .filter((key) => selectedFaults[key] === true)
      .map(Number);

    // Get option IDs for selected faults that have options
    const optionIds = [];
    Object.keys(selectedOptions).forEach((faultId) => {
      const optionId = selectedOptions[faultId];
      if (optionId && selectedFaults[faultId]) {
        optionIds.push(optionId);
      }
    });

    const params = {
      brandSlug: brandSlug,
      productSlug: productSlug,
      variationSlug: variantSlug,
      deviceAge: getDeviceAgeValue(),
      cityId,
      faultIds,
      upgradeIds: selectedUpgrades,
      optionIds,
    };

    if (!deviceAge) {
      toast.error("Please select the device age");
      return;
    }

    if (!cityId) {
      toast.error("Please select your city");
      return;
    }

    if (!isLoggedIn || !getAuthToken()) {
      calculationParams.current = params;
      setShowLoginModal(true);
      return;
    }

    await executeCalculation(params);
  };

  // Never auto-calculate on isLoggedIn changing: the auth slice flips to
  // logged-in as soon as OTP succeeds, BEFORE the Login/SignUp tracking POST.
  // The modal's onSuccess below is called only after tracking has settled.

  // Handle errors from slice
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleToggleFault = (faultId, category) => {
    dispatch(toggleFault({ faultId, category }));
  };

  const handleToggleOption = ({ faultId, optionId }) => {
    dispatch(toggleOption({ faultId, optionId }));
  };

  const handleToggleUpgrade = (upgradeId) => {
    dispatch(toggleUpgrade(upgradeId));
  };

  const handleNext = () => {
    dispatch(nextStep());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrev = () => {
    dispatch(prevStep());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // Fired only after Login / SignUp tracking has been awaited by LoginModal.
    // Reuse the captured fault selections; calculate exactly once.
    const params = calculationParams.current;
    calculationParams.current = null;
    if (params && !hasRedirected.current && !isCalculating.current) {
      void executeCalculation(params);
    }
  };

  const handleLoginClose = () => {
    // Only a user-initiated close cancels the saved calculation. Successful
    // authentication uses handleLoginSuccess instead of this cancellation path.
    setShowLoginModal(false);
    calculationParams.current = null;
  };

  const canProceed = useCallback(() => {
    if (isAgeStep) {
      return !!deviceAge;
    }
    if (isUpgradeStep) {
      const upgradeGroups = upgradeOptions || [];
      for (const group of upgradeGroups) {
        const hasSelection = group.options?.some((opt) =>
          selectedUpgrades.includes(opt.id),
        );
        if (!hasSelection) return false;
      }
      return true;
    }
    return true;
  }, [isAgeStep, isUpgradeStep, deviceAge, upgradeOptions, selectedUpgrades]);

  if (loading) {
    return (
      <Layout>
        <CalculatorSkeleton isDarkMode={isDarkMode} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl px-3 py-4 mx-auto sm:px-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {/* Step 0: Upgrade Options - Only show if upgrades exist */}
            {isUpgradeStep && hasUpgrades && (
              <UpgradeOptions
                upgradeOptions={upgradeOptions}
                selectedUpgrades={selectedUpgrades}
                onToggleUpgrade={handleToggleUpgrade}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Step 1: Age Selection */}
            {isAgeStep && (
              <DeviceAgeSelector
                deviceAge={deviceAge}
                ageRanges={ageRanges || []}
                onSelect={(value) => dispatch(setDeviceAge(value))}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Fault Steps - One category per step */}
            {faultStepIndex >= 0 &&
              faultStepIndex < stepCategories.length &&
              currentCategories.length > 0 && (
                <div className="space-y-4">
                  {currentCategories.map((category) => {
                    return (
                      <FaultCategory
                        key={category.id}
                        category={category}
                        selectedFaults={selectedFaults}
                        selectedOptions={selectedOptions}
                        onToggleFault={handleToggleFault}
                        onToggleOption={handleToggleOption}
                        isDarkMode={isDarkMode}
                      />
                    );
                  })}
                </div>
              )}

            <NavigationButtons
              currentStep={currentStep}
              isLastStep={isLastStep}
              onPrev={handlePrev}
              onNext={handleNext}
              onSubmit={handleCalculate}
              submitting={calculating}
              isDarkMode={isDarkMode}
              canProceed={canProceed()}
              isUpgradeStep={isUpgradeStep}
              isAgeStep={isAgeStep}
              upgradeOptions={upgradeOptions}
              selectedUpgrades={selectedUpgrades}
              stepCategories={stepCategories}
              hasSelectedFaultsInStep={hasSelectedFaultsInStep}
            />
          </div>

          <div className="lg:col-span-1">
            <DeviceInfoCard
              productData={productData}
              deviceAge={deviceAge}
              selectedFaults={selectedFaults}
              selectedOptions={selectedOptions}
              faultCategories={faultCategories}
              upgradeOptions={upgradeOptions}
              selectedUpgrades={selectedUpgrades}
              ageRanges={ageRanges}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>

      <LoginModal
        visible={showLoginModal}
        onClose={handleLoginClose}
        onSuccess={handleLoginSuccess}
        productData={productData}
      />
    </Layout>
  );
};

export default Calculator;
